import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { type ReactNode, useRef } from 'react';

// Timings from design B9
const ENTER_EASE: [number, number, number, number] = [0.2, 0.7, 0.2, 1];
const EXIT_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];

const screenVariants: Variants = {
  initial: (dir: number) => ({
    x: dir >= 0 ? 30 : -30,
    opacity: 0,
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.42, ease: ENTER_EASE },
  },
  exit: (dir: number) => ({
    x: dir >= 0 ? -22 : 22,
    opacity: 0,
    pointerEvents: 'none' as const,
    transition: { duration: 0.4, ease: EXIT_EASE },
  }),
};

// Both layers are absolutely positioned and overlap during transition (~0.56s).
// Parent container MUST be position:relative with a defined size.
const layerStyle = {
  position: 'absolute' as const,
  inset: 0,
  display: 'flex',
  flexDirection: 'column' as const,
};

interface Props {
  motionKey: string | number;
  direction: number;
  children: ReactNode;
}

// Directed slide+fade transition (B9). reduced-motion handled by MotionRoot MotionConfig.
// AnimatePresence is NOT mode="wait" — old and new layers overlap during the transition.
export function ScreenTransition({ motionKey, direction, children }: Props) {
  return (
    <AnimatePresence custom={direction}>
      <motion.div
        key={motionKey}
        custom={direction}
        variants={screenVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={layerStyle}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Ordered room statuses for direction computation (Archived is terminal, excluded).
export const ROOM_STATUS_ORDER = [
  'WaitingForPlayers',
  'InGame',
  'BetweenRounds',
  'Finished',
] as const;

// Tracks previous key and returns direction (+1 forward, -1 backward).
// Call during render (not in effect) so AnimatePresence gets the correct custom value.
export function useScreenDirection(currentKey: string, order: ReadonlyArray<string>): number {
  const prevKeyRef = useRef<string>(currentKey);
  const dirRef = useRef<number>(1);

  if (prevKeyRef.current !== currentKey) {
    const pi = order.indexOf(prevKeyRef.current);
    const ci = order.indexOf(currentKey);
    dirRef.current = pi === -1 || ci === -1 || ci >= pi ? 1 : -1;
    prevKeyRef.current = currentKey;
  }

  return dirRef.current;
}
